import * as React from "react";
import { SortDirections, Waffles, WafflesPaginationProps } from "@app/shared/waffles";
import { FC, JSX, useMemo } from "react";
import { TableType, ViewableField } from "@app/viewable-fields";
import { NextClosestImpactProviderTableData, NextClosestImpactProviderTableDataWithCargo } from "./types";
import { buildAdequacyProvidersColumnConfig } from "./ServicingProviderHelpers";
import { ProviderNetworkType } from "../enumerators";
import { ProjectOptions } from "..";
import ProjectRunConfiguration from "../projectRunConfiguration";
import { Features } from "@app/feature-flags";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";

type DisplayedProviderType = "excluded" | "nonExcluded";

interface NextClosestImpactProviderTableProps {
  bulkActionBar: JSX.Element;
  data: (NextClosestImpactProviderTableData | NextClosestImpactProviderTableDataWithCargo)[];
  displayedProviderType: DisplayedProviderType;
  providerType: ProviderNetworkType;
  handleSort: (col: keyof NextClosestImpactProviderTableDataWithCargo, direction: SortDirections) => void;
  loading: boolean;
  project: ProjectOptions;
  runConfiguration: ProjectRunConfiguration;
  onSelectedProvidersChanged: React.Dispatch<React.SetStateAction<string[]>>
  selectedProviders: string[];
  setSkip: (newSkip: number) => void;
  showDist: boolean;
  showTime: boolean;
  skip: number;
  tableType: TableType;
  take: number;
  totalRecords: number;
  viewableFields: ViewableField[];
}

export const NextClosestImpactProviderTable: FC<React.PropsWithChildren<NextClosestImpactProviderTableProps>> = (props: NextClosestImpactProviderTableProps) => {
  const featureFlags = React.useContext(FeatureFlagContext).enabledFeatureFlags;
  const columnConfig = useMemo(() => {
    const isExclusion = props.displayedProviderType === "excluded";
    return buildAdequacyProvidersColumnConfig(
      props.viewableFields,
      props.tableType,
      props.project.showNppesDates && props.runConfiguration && props.runConfiguration.showNppesDates,
      props.project.showMipsScore && props.runConfiguration && props.runConfiguration.showMipsScore,
      props.project.showPciData && props.runConfiguration && props.runConfiguration.showPciData,
      props.project.showVaClaimsVolumeColumn,
      props.project.showTricareClaimsVolumeColumn,
      props.project.showAverageDistance && props.showDist,
      props.project.showAverageTime && props.showTime,
      isExclusion,
      true,
      isExclusion,
      props.project.showLocationConfidenceScores && props.runConfiguration?.showLocationConfidenceScores,
      props.project.showMotiveAppropriatenessScore && props.runConfiguration?.showMotiveAppropriatenessScore,
      featureFlags.has(Features.PCI_DASHBOARD_NEW_PATIENT_CPT),
      featureFlags.has(Features.ENABLE_CARE_JOURNEY_SCORES) && props.runConfiguration?.showCareJourneyScores
    );

  }, [props.viewableFields, props.tableType, props.project, props.runConfiguration, props.displayedProviderType, props.showDist, props.showTime]);

  const paginationProps: WafflesPaginationProps = {
    changePage: (newPageNumber) => { props.setSkip((newPageNumber - 1) * props.take) },
    recordsPerPage: props.take,
    skip: props.skip,
    totalRecords: props.totalRecords,
  };

  return (
    <Waffles<NextClosestImpactProviderTableData | NextClosestImpactProviderTableDataWithCargo>
      bulkActionBar={props.bulkActionBar}
      columns={columnConfig}
      customSort={(segment, col, direction) => { props.handleSort(col, direction); return segment; }}
      data={props.data}
      enablePagination={true}
      fullWidth={true}
      handleCheckboxChange={props.onSelectedProvidersChanged}
      loading={props.loading}
      paginationProps={paginationProps}
      passThroughSorting={true}
      selectedKeys={props.selectedProviders}
      showCheckboxes={true}
      testId={`next-closest-${props.providerType}-provider-table`}
    />
  );
}